<template>

    <div class="af-job-apply-form-guest-user afu-my-8">
        <div class="afu-font-semibold afu-text-4xl afu-leading-4xl afu-mb-8 afu-text-black">
            {{ $t('apply.apply_with_email', {email: applyEmail}) }}
        </div>

        <ValidationObserver
            ref="formSeeker"
            tag="form"
            class="af-job-apply-form"
            @submit.prevent="apply()"
        >
            <ApplyPersonalInfo ref="personalInfo" />

            <ApplyDocuments ref="applyDocuments" />

            <ScreeningQuestion 
                v-if="showScreeningQuestion"
                :job-uuid="$_job.uuid"
                :on-apply-page=true
                ref="screeningQuestions"
            />

            <C1Alert
                v-if="!!response.message"
                :variant="response.status"
            >
                <span v-html="response.message" />
            </C1Alert>

            <ApplyFormFooter />

            <div class="afu-text-center afu-mt-6">
                <MqLayout mq="sm">
                    <C1Button
                        type="submit"
                        size="lg"
                        class="af-btn-apply-submit af-btn-apply-submit"
                        :is-busy="loader.apply"
                        variant="primary"
                        block
                    >
                        {{ $t('apply.apply_for_job') }}
                    </C1Button>
                </MqLayout>
                <MqLayout mq="tsm+">
                    <C1Button
                        type="submit"
                        size="lg"
                        class="af-btn-apply-submit af-btn-apply-submit afu-w-200"
                        :is-busy="loader.apply"
                        variant="primary"
                    >
                        {{ $t('apply.apply_for_job') }}
                    </C1Button>
                </MqLayout>
            </div>
        </ValidationObserver>
    </div>
</template>

<script>
    import has  from 'lodash/has';
    import Vue from 'vue';
    import {mapState, mapActions} from 'vuex';
    import { ValidationObserver } from 'vee-validate';
    import C1Button from '@/components/C1Components/C1Button';
    import C1Alert from '@/components/C1Components/C1Alert';
    import ApplyPersonalInfo from './ApplyPersonalInfo';
    import ApplyDocuments from './ApplyDocuments';
    import ApplyFormFooter from './ApplyFormFooter';
    import ApplyMixin from './ApplyMixin';
    import mixpanel from 'mixpanel-browser';
    import ScreeningQuestion from './ScreeningQuestion';

    //for tracking headers
    import axios from 'axios';
    // or with require() syntax:
    // const mixpanel = require('mixpanel-browser');

    // Enabling the debug mode flag is useful during implementation,
    // but it's recommended you remove it for production
    mixpanel.init(`${process.env.VUE_APP_MIXPANEL_TOKEN}`, {ignore_dnt: true});
    export default {
        name: 'ApplyForm',

        components: {
            ValidationObserver,
            ApplyPersonalInfo,
            ApplyDocuments,
            C1Button,
            C1Alert,
            ApplyFormFooter,
            ScreeningQuestion,
        },

        mixins: [ApplyMixin],

        data() {
            return {
                response: {
                    status: '',
                    message: '',
                },
                loader: {
                    apply: false,
                },
            };
        },

        computed: {
            ...mapState({
                $_job: state => state.job.job,
                $_apiMessage: state => state.apiMessage,
                $_jobApplication: state => state.jobApplication,
                $_me: state => state.me.user,
                $_applyUser: state => state.jobApplication.user,
            }),

            applyEmail: {
                get() {
                    return this.$_applyUser.email || this.$_me.email;
                },
                set(newValue) {
                    Vue.set(this.$store.state.jobApplication.user, 'email', newValue);
                },
            },

            showScreeningQuestion() {
                return window?.afConfig?.screening_question_position?.toLowerCase() === 'before job apply';
            },
        },

        methods: {
            ...mapActions('jobApplication', {
                $_applyToJob: 'applyToJob',
            }),

            mounted() {
                window.eventBus.$emit('apply-form-plugin-loaded', { job_uuid: this.$_job.uuid } );
            },

            async apply() {
                const isResumeAttachmentOpt = this.isResumeAttachmentOptional();
                if (isResumeAttachmentOpt) {
                    console.log(isResumeAttachmentOpt);
                }
                else {
                    if (!has(this.$refs.applyDocuments.$refs.resume, 'selectedResume.id')) {
                        this.response.status = 'danger';
                        this.response.message = 'Resume is mandatory';
                        return false;
                    }
                }
             
                const validation = await this.$refs.formSeeker.validate();
                if (!validation) {
                    return false;
                }

                this.response.status = '';
                this.response.message = '';

                window.eventBus.$emit('apply-form-submited', { job_uuid: this.$_job.uuid} );

                const data = {
                    token: this.$_job.token,
                };


                data.first_name = this.$_jobApplication.user.first_name;
                data.last_name = this.$_jobApplication.user.last_name;
                data.contact_number = this.$_jobApplication.user.contact_number;

                // Set resume
                if (has(this.$refs.applyDocuments.$refs.resume, 'selectedResume.id')) {
                    data.resume_id = this.$refs.applyDocuments.$refs.resume.selectedResume.id;
                }

                // // Set cover letter
                // if (has(this.$refs.applyDocuments.$refs.coverLetter, 'selectedCoverLetter.id')) {
                //     data.cover_letter_id = this.$refs.applyDocuments.$refs.coverLetter.selectedCoverLetter.id;
                // }

                // Set cover letters and additional docs
                let core_file_ids = [];
                if (has(this.$refs.applyDocuments.$refs.additionalDocument, 'selectedAdditionalDocuments')) {
                    const additionalDocs = this.$refs.applyDocuments.$refs.additionalDocument.selectedAdditionalDocuments;
                    additionalDocs.forEach((o) => {
                        if (o.id) {
                            core_file_ids.push(o.id);
                        }
                    });
                }

                if (has(this.$refs.applyDocuments.$refs.coverLetter, 'selectedCoverLetter')) {
                    const coverLetter = this.$refs.applyDocuments.$refs.coverLetter.selectedCoverLetter;
                    if (coverLetter.id) {
                        core_file_ids.push(coverLetter.id);
                    }
                }

                data.core_file_ids = core_file_ids;

                if (!isResumeAttachmentOpt){
                    data.make_resume_default = this.$_jobApplication.make_resume_default;
                }
                
                data.utm = this.getUtmParameters();

                this.loader.apply = true;
                if (this.$refs.screeningQuestions) {
                    data.screening_questions = this.$refs.screeningQuestions.getResponses();
                }
                this.$_applyToJob({ jobUuid: this.$_job.uuid, data }).then((response) => {
                    this.afterApply(response.data);
                    mixpanel.track('Job application success', {
                        'source': 'Apply to job - ApplyForm.vue',
                        'submittedData': data,
                        'userData': this.$_applyUser || this.$_me,
                        'headers': axios.defaults.headers,
                    });
                }).catch((error) => {
                    const errorCode = error.response.data.error;
                    this.response.status = 'danger';
                    this.response.message = this.$t(`api_message.${errorCode}`);
                    this.loader.apply = false;
                    mixpanel.track('Job application failed', {
                        'source': 'Apply to job - Applyform.vue',
                        'errorData': error.response.data,
                        'errorStatus': errorCode,
                        'submittedData': data,
                        'userData': this.$_applyUser || this.$_me,
                        'headers': axios.defaults.headers,
                    });
                });
            },
        },
    };
</script>
